import * as React from "react"
import { dateOnlyToDatePickerValue, datePickerValueToDateOnly } from "@ibada/shared-types"

import { cn } from "../../lib/utils"
import { DatePicker } from "./date-picker"

function parseDateInputValue(value: unknown) {
  if (typeof value !== "string") return undefined
  return dateOnlyToDatePickerValue(value) ?? undefined
}

function formatDateInputValue(date?: Date) {
  return datePickerValueToDateOnly(date) ?? ""
}

function setNativeInputValue(input: HTMLInputElement, value: string) {
  const valueSetter = Object.getOwnPropertyDescriptor(input, "value")?.set
  const prototype = Object.getPrototypeOf(input) as HTMLInputElement
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, "value")?.set

  if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter.call(input, value)
    return
  }

  input.value = value
}

function DateInput({
  className,
  value,
  defaultValue,
  onChange,
  onBlur,
  disabled,
  placeholder,
  id,
  name,
  required,
  form,
  min,
  max,
  "aria-describedby": ariaDescribedBy,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
}: React.ComponentProps<"input">) {
  const hiddenInputRef = React.useRef<HTMLInputElement>(null)
  const controlledValue =
    typeof value === "string"
      ? value
      : typeof value === "number"
        ? String(value)
        : undefined
  const uncontrolledDefaultValue =
    typeof defaultValue === "string"
      ? defaultValue
      : typeof defaultValue === "number"
        ? String(defaultValue)
        : undefined
  const [internalValue, setInternalValue] = React.useState(uncontrolledDefaultValue ?? "")
  const dateValue = parseDateInputValue(controlledValue ?? internalValue)
  const minDate = parseDateInputValue(min)
  const maxDate = parseDateInputValue(max)
  const calendarDisabled = React.useMemo(() => {
    const rules: Array<{ before: Date } | { after: Date }> = []
    if (minDate) rules.push({ before: minDate })
    if (maxDate) rules.push({ after: maxDate })
    return rules.length > 0 ? rules : undefined
  }, [maxDate, minDate])

  const emitInputChange = React.useCallback((nextValue: string) => {
    const input = hiddenInputRef.current
    if (!input) return

    setNativeInputValue(input, nextValue)
    onChange?.({
      target: input,
      currentTarget: input,
    } as React.ChangeEvent<HTMLInputElement>)
  }, [onChange])

  const handleChange = React.useCallback((date?: Date) => {
    const nextValue = formatDateInputValue(date)
    if (controlledValue === undefined) {
      setInternalValue(nextValue)
    }
    emitInputChange(nextValue)
  }, [controlledValue, emitInputChange])

  const handleBlur = React.useCallback(() => {
    const input = hiddenInputRef.current
    if (!input) return

    onBlur?.({
      target: input,
      currentTarget: input,
    } as React.FocusEvent<HTMLInputElement>)
  }, [onBlur])

  return (
    <>
      <DatePicker
        id={id}
        value={controlledValue !== undefined ? dateValue ?? null : dateValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={typeof placeholder === "string" ? placeholder : undefined}
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        calendarDisabled={calendarDisabled}
        className={cn(
          "border-border focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive disabled:bg-input/70 placeholder:text-muted-foreground h-control w-full min-w-0 rounded-lg border bg-background dark:bg-input shadow-[var(--shadow-field)] px-2.5 py-1 text-base text-input-foreground transition-colors outline-none focus-visible:ring-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-3 md:text-sm",
          className,
        )}
      />
      <input
        ref={hiddenInputRef}
        type="hidden"
        name={name}
        form={form}
        required={required}
        min={min}
        max={max}
        disabled={disabled}
        value={controlledValue ?? internalValue}
        onChange={onChange}
        readOnly={!onChange}
        data-slot="input-date-value"
      />
    </>
  )
}

const inputClassName =
  "border-border focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive disabled:bg-input/70 file:text-foreground placeholder:text-muted-foreground h-control w-full min-w-0 rounded-lg border bg-background dark:bg-input shadow-[var(--shadow-field)] px-2.5 py-1 text-base text-input-foreground transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-3 md:text-sm"

type InputProps = React.ComponentProps<"input"> & {
  /** Show a live `used/max` character counter as a suffix inside the field. Requires `maxLength`. */
  showCount?: boolean
}

function Input({ className, type, showCount, ...props }: InputProps) {
  if (type === "date") {
    return <DateInput className={className} type={type} {...props} />
  }

  const input = (
    <input
      type={type}
      data-slot="input"
      data-control=""
      className={cn(inputClassName, showCount && typeof props.maxLength === "number" && "pr-14", className)}
      {...props}
    />
  )

  if (!showCount || typeof props.maxLength !== "number") return input

  const length = typeof props.value === "string" || typeof props.value === "number" ? String(props.value).length : 0

  return (
    <div className='relative'>
      {input}
      <span
        aria-hidden='true'
        className='text-muted-foreground pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-xs tabular-nums'
      >
        {length}/{props.maxLength}
      </span>
    </div>
  )
}

export { Input }
